import { TIconButtonType } from "@shared/components/ui/icon-button";

export interface IEntity {
  id?: any;
}

// Define the ButtonConfig type
/**
 * Configuration object for button properties
 */
export type ButtonConfig = {
  /** The CSS class for the flat icon */
  flatIcon: string;
  /** The text or translation key to display on the button */
  text: string;
  /** The HTML button type */
  type: 'button' | 'submit' | 'reset';
  /** Optional click handler function */
  onClick?: () => void;
};

export enum EActionsMenuGroup {
  Selection = 'selection',
  Report = 'report',
}

export enum EActionsMenuScope {
  Grid = 'grid',
  Report = 'report',
}

export enum EActionsMenuOutcome {
  Done = 'done',
  Delivered = 'delivered',
  Empty = 'empty',
  SelectionRequired = 'selection-required',
}

export type TActionsMenuSelectionGuide = {
  titleKey: string;
  descriptionKey: string;
}

export type TActionsMenuResult = {
  outcome: EActionsMenuOutcome;
  count?: number;
  messageKey?: string;
  hintKey?: string;
  selectionGuide?: TActionsMenuSelectionGuide;
}

export type TExportRowsOptions = {
  limitConfirmed?: boolean;
}

export type TCustomExportOption = {
  id?: number | string;
  description?: string;
  actionsMenuDescription?: string;
}

export type TCustomExportRunner = (option: TCustomExportOption, rowsOptions: TExportRowsOptions) => Promise<TActionsMenuResult>;

export type TActionsMenuTask = {
  id: string;
  loadingKey: string;
  errorKey: string;
  emptyKey: string;
  usesExportLimit: boolean;
  run: (rowsOptions: TExportRowsOptions) => Promise<TActionsMenuResult>;
}

export type TGridActionsMenuHost = {
  exportExcel: (rowsOptions: TExportRowsOptions) => Promise<TActionsMenuResult>;
  exportPdf: (rowsOptions: TExportRowsOptions, mobile: boolean) => Promise<TActionsMenuResult>;
  exportLimitOverflow: () => number | null;
}

export type TRightTopIconButton = {
  type: TIconButtonType;
  icon: string;
  tooltipContent: string;
  action: () => void | Promise<TActionsMenuResult | void>;
  disabled?: boolean;
  description?: string;
  actionsMenuGroup?: EActionsMenuGroup;
  loadingLabel?: string;
  errorLabel?: string;
}

// localStorage behind `[persistFilterKey]`: one key per grid, the prefix below
// suffixed with that key. `DxCustomDataGridComponent` is the only writer — a
// host names the key, opts into how much is remembered via
// `[persistFullGridState]`, and reads state back off the grid. A host that
// writes here itself ends up with two copies racing on the same `contentReady`.

/**
 * The whole snapshot: header filters, filter row, column layout, page index and
 * search text (payload `v: 2`). One key, written in one place — the search used
 * to live in a key of its own, which only bought it a second lifecycle to fall
 * out of sync with.
 */
export const PERSISTED_GRID_FILTERS_PREFIX = 'dx-grid-filters/';

/**
 * Retired prefixes, still on disk for anyone who used the app before the
 * snapshot was consolidated. Nothing reads them any more, so they would sit
 * there forever — one entry per grid, per user — unless swept; see
 * `DxCustomDataGridComponent.purgeLegacyPersistedKeys`.
 *
 * The `dx-report-*` names are historical: they predate the generic grid taking
 * the feature over and were shared by every grid, not just reports.
 */
export const LEGACY_PERSISTED_GRID_PREFIXES = [
  'dx-report-columns/',
  'dx-report-search/',
] as const;
